import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  CogIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import configuratorService from '../services/configuratorService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
  }

  &.secondary {
    background: ${({ theme }) => theme.cardBg};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.border};

    &:hover {
      border-color: #3b82f6;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.border};

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ color }) => `${color}20`};
    color: ${({ color }) => color};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .info {
    .value {
      font-size: 1.5rem;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
    }
    .label {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.textSecondary};
    }
  }
`;

const ConfiguratorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const ConfiguratorCard = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const Logo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${({ color }) => color || '#3b82f6'} 0%, ${({ color }) => color || '#2563eb'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
`;

const CardInfo = styled.div`
  flex: 1;

  .name {
    font-size: 1.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 4px;
  }

  .slug {
    font-size: 0.875rem;
    color: ${({ theme }) => theme.textSecondary};
    font-family: monospace;
    background: ${({ theme }) => theme.bgSecondary};
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background: #dcfce7;
    color: #166534;
  }

  &.inactive {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const CardBody = styled.div`
  padding: 20px;

  .description {
    color: ${({ theme }) => theme.textSecondary};
    font-size: 0.875rem;
    margin-bottom: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    .meta-item {
      .label {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.textSecondary};
        text-transform: uppercase;
        margin-bottom: 4px;
      }
      .value {
        font-size: 0.875rem;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
      }
    }
  }
`;

const CardFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.bgSecondary};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.cardBg};
    color: ${({ theme }) => theme.text};
  }

  &.delete:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};

  svg {
    width: 64px;
    height: 64px;
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 8px;
  }

  p {
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 24px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid ${({ theme }) => theme.border};
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 24px;
  max-width: 500px;
  width: 90%;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 12px;
    color: ${({ theme }) => theme.text};
  }

  p {
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 24px;
  }

  .actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }
`;

const EmbedCodeBox = styled.div`
  background: #1f2937;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
  
  pre {
    color: #e5e7eb;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 12px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #374151;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #4b5563;
  }
  
  &.copied {
    background: #16a34a;
  }
`;

const EmbedOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  
  input {
    width: 16px;
    height: 16px;
  }
`;

const Configurators = () => {
  const navigate = useNavigate();
  const [configurators, setConfigurators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, totalOrders: 0 });
  const [deleteModal, setDeleteModal] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [embedModal, setEmbedModal] = useState(null);
  const [embedType, setEmbedType] = useState('script');
  const [copied, setCopied] = useState(false);

  const getEmbedCode = (config) => {
    const baseUrl = window.location.origin;
    
    if (embedType === 'script') {
      return `<!-- Magelan Configurator Widget -->
<div id="magelan-configurator" data-configurator="${config.slug}"></div>
<script src="${baseUrl}/embed/configurator.js"></script>`;
    } else if (embedType === 'advanced') {
      return `<!-- Magelan Configurator Widget -->
<div id="my-configurator"></div>
<script src="${baseUrl}/embed/configurator.js"></script>
<script>
  MagelanConfigurator.init({
    container: '#my-configurator',
    configurator: '${config.slug}',
    baseUrl: '${baseUrl}',
    theme: 'light',
    onComplete: function(order) {
      console.log('Order submitted:', order);
    }
  });
</script>`;
    } else {
      // iframe
      return `<iframe 
  src="${baseUrl}/configurator/${config.slug}?embed=true" 
  width="100%" 
  height="800" 
  frameborder="0"
  style="border: none;"
></iframe>`;
    }
  };

  const handleCopyEmbed = async () => {
    if (!embedModal) return;
    const config = configurators.find(c => c.id === embedModal);
    if (!config) return;
    
    const code = getEmbedCode(config);
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    loadConfigurators();
  }, [includeInactive]);

  const loadConfigurators = async () => {
    try {
      setLoading(true);
      const data = await configuratorService.getConfigurators(includeInactive);
      setConfigurators(data.configurators || []);
      
      // Calculate stats
      const active = (data.configurators || []).filter(c => c.is_active).length;
      setStats({
        total: (data.configurators || []).length,
        active,
        totalOrders: 0 // TODO: Fetch from API
      });
    } catch (error) {
      console.error('Error loading configurators:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await configuratorService.duplicateConfigurator(id);
      loadConfigurators();
    } catch (error) {
      console.error('Error duplicating configurator:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await configuratorService.deleteConfigurator(deleteModal);
      setDeleteModal(null);
      loadConfigurators();
    } catch (error) {
      console.error('Error deleting configurator:', error);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Title>Product Configurators</Title>
        <HeaderActions>
          <Button
            className="secondary"
            onClick={() => setIncludeInactive(!includeInactive)}
          >
            {includeInactive ? 'Hide Inactive' : 'Show Inactive'}
          </Button>
          <Button className="secondary" onClick={() => navigate('/configurators/analytics')}>
            📊 Analytics
          </Button>
          <Button className="secondary" onClick={() => navigate('/configurators/pipeline-settings')}>
            <CogIcon />
            Pipeline Settings
          </Button>
          <Button className="primary" onClick={() => navigate('/configurators/new')}>
            <PlusIcon />
            New Configurator
          </Button>
        </HeaderActions>
      </PageHeader>

      <StatsBar>
        <StatCard color="#3b82f6">
          <div className="icon-wrapper">
            <CogIcon />
          </div>
          <div className="info">
            <div className="value">{stats.total}</div>
            <div className="label">Total Configurators</div>
          </div>
        </StatCard>
        <StatCard color="#10b981">
          <div className="icon-wrapper">
            <EyeIcon />
          </div>
          <div className="info">
            <div className="value">{stats.active}</div>
            <div className="label">Active</div>
          </div>
        </StatCard>
        <StatCard color="#8b5cf6">
          <div className="icon-wrapper">
            <ClipboardDocumentListIcon />
          </div>
          <div className="info">
            <div className="value">{stats.totalOrders}</div>
            <div className="label">Total Orders</div>
          </div>
        </StatCard>
      </StatsBar>

      {configurators.length === 0 ? (
        <EmptyState>
          <CogIcon />
          <h3>No Configurators Yet</h3>
          <p>Create your first product configurator to start accepting custom orders.</p>
          <Button className="primary" onClick={() => navigate('/configurators/new')}>
            <PlusIcon />
            Create Configurator
          </Button>
        </EmptyState>
      ) : (
        <ConfiguratorsGrid>
          {configurators.map((config) => (
            <ConfiguratorCard key={config.id}>
              <CardHeader>
                <Logo color={config.primary_color}>
                  {config.logo_url ? (
                    <img src={config.logo_url} alt={config.name} />
                  ) : (
                    getInitials(config.name)
                  )}
                </Logo>
                <CardInfo>
                  <div className="name">{config.name}</div>
                  <div className="slug">/{config.slug}</div>
                </CardInfo>
                <StatusBadge className={config.is_active ? 'active' : 'inactive'}>
                  {config.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </CardHeader>
              <CardBody>
                <div className="description">
                  {config.description || 'No description provided.'}
                </div>
                <div className="meta">
                  <div className="meta-item">
                    <div className="label">Sections</div>
                    <div className="value">{config.sections_count || 0}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Currency</div>
                    <div className="value">{config.currency || 'EUR'}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">VAT Rate</div>
                    <div className="value">{config.vat_rate || 0}%</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Guest Orders</div>
                    <div className="value">{config.allow_guest_orders ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              </CardBody>
              <CardFooter>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ActionButton onClick={() => navigate(`/configurators/${config.id}`)}>
                    <PencilIcon />
                    Edit
                  </ActionButton>
                  <ActionButton onClick={() => handleDuplicate(config.id)}>
                    <DocumentDuplicateIcon />
                    Duplicate
                  </ActionButton>
                  <ActionButton onClick={() => setEmbedModal(config.id)}>
                    🔗 Embed
                  </ActionButton>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ActionButton onClick={() => window.open(`/configurator/${config.slug}`, '_blank')}>
                    👁️ Preview
                  </ActionButton>
                  <ActionButton onClick={() => navigate(`/configurators/${config.id}/orders`)}>
                    <ChartBarIcon />
                    Orders
                  </ActionButton>
                  <ActionButton className="delete" onClick={() => setDeleteModal(config.id)}>
                    <TrashIcon />
                  </ActionButton>
                </div>
              </CardFooter>
            </ConfiguratorCard>
          ))}
        </ConfiguratorsGrid>
      )}

      {deleteModal && (
        <Modal onClick={() => setDeleteModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Delete Configurator?</h3>
            <p>
              This action cannot be undone. All sections and items will be permanently deleted.
            </p>
            <div className="actions">
              <Button className="secondary" onClick={() => setDeleteModal(null)}>
                Cancel
              </Button>
              <Button
                className="primary"
                style={{ background: '#dc2626' }}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {embedModal && (
        <Modal onClick={() => setEmbedModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>🔗 Embed Configurator</h3>
            <p>
              Copy the code below to embed this configurator on your website.
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <EmbedOption>
                <input 
                  type="radio" 
                  name="embedType" 
                  checked={embedType === 'script'} 
                  onChange={() => setEmbedType('script')}
                />
                Simple (Recommended)
              </EmbedOption>
              <EmbedOption>
                <input 
                  type="radio" 
                  name="embedType" 
                  checked={embedType === 'advanced'} 
                  onChange={() => setEmbedType('advanced')}
                />
                Advanced (with callbacks)
              </EmbedOption>
              <EmbedOption>
                <input 
                  type="radio" 
                  name="embedType" 
                  checked={embedType === 'iframe'} 
                  onChange={() => setEmbedType('iframe')}
                />
                iFrame (fallback)
              </EmbedOption>
            </div>
            
            <EmbedCodeBox>
              <CopyButton 
                onClick={handleCopyEmbed}
                className={copied ? 'copied' : ''}
              >
                {copied ? '✓ Copied!' : 'Copy'}
              </CopyButton>
              <pre>
                {configurators.find(c => c.id === embedModal) && 
                  getEmbedCode(configurators.find(c => c.id === embedModal))}
              </pre>
            </EmbedCodeBox>
            
            <div className="actions">
              <Button className="secondary" onClick={() => setEmbedModal(null)}>
                Close
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Configurators;
